/**
 * FR-018.7 — the re-home job after a confirm (`queued → running → done`, outcome sentences, a
 * link to the target), shared by the mapping editor and the group editor.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContextPanel } from '@hofsync/design-system';
import { sourcesErrorKind, type MappingJob } from '../../../../services/sourcesService';
import { jobOutcomeSentences, type Translate } from './previewSentences';
import type { JobState } from './usePreviewConfirm';
import './MappingEditorPanel.css';

export interface MappingJobViewProps {
    title: string;
    subtitle?: string;
    job: JobState;
    jobQuery: { data?: MappingJob; error?: unknown };
    noun: string;
    onClose(): void;
    onNavigate?(entityType: string, entityId: string): void;
}

export const MappingJobView: React.FC<MappingJobViewProps> = ({ title, subtitle, job, jobQuery, noun, onClose, onNavigate }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as unknown as Translate;
    const status = jobQuery.data?.status ?? 'queued';
    const jobError = jobQuery.error
        ? t(
              `farm.sources.errors.${sourcesErrorKind(jobQuery.error) ?? '_'}`,
              t('farm.sources.errors.sources_job_unavailable', 'Der Stand des Vorgangs ist gerade nicht abrufbar.'),
          )
        : null;
    const closeButton = (
        <button type="button" className="mapping-editor__btn mapping-editor__btn--outline" onClick={onClose}>
            {t('farm.sources.editor.close', 'Schließen')}
        </button>
    );
    return (
        <ContextPanel isOpen onClose={onClose} title={title} subtitle={subtitle} width="medium" footerSlot={closeButton}>
            <div className="mapping-editor mapping-editor--job" aria-live="polite">
                {job.jobId === null ? (
                    <p className="mapping-editor__status">{t('farm.sources.editor.saved', 'Gespeichert.')}</p>
                ) : jobError ? (
                    <p className="mapping-editor__reason">{jobError}</p>
                ) : (
                    <>
                        <p className="mapping-editor__status">{t(`farm.sources.job.${status}`, status)}</p>
                        {status === 'done' && (
                            <>
                                <ul className="mapping-editor__sentences">
                                    {jobOutcomeSentences(jobQuery.data?.outcome, t).map((s) => (
                                        <li key={s}>{s}</li>
                                    ))}
                                </ul>
                                {onNavigate && job.targetKind === 'entity' && (
                                    <button
                                        type="button"
                                        className="mapping-editor__link"
                                        onClick={() => {
                                            onNavigate(job.targetType, job.targetId);
                                            onClose();
                                        }}
                                    >
                                        {t('farm.sources.editor.openTarget', '{{noun}} öffnen', { noun })}
                                    </button>
                                )}
                            </>
                        )}
                        {(status === 'refused' || status === 'failed') && (
                            <p className="mapping-editor__reason">
                                {t('farm.sources.editor.jobNotApplied', 'Die Änderung wurde nicht ausgeführt. Bitte später erneut prüfen.')}
                            </p>
                        )}
                    </>
                )}
            </div>
        </ContextPanel>
    );
};

export default MappingJobView;
